import { useEffect, useState } from "react";
import InfoIconWithTooltip from "src/common/components/Icon/InfoIconWithTooltip";

// Controlled open/onOpenChange: when open is provided the caller owns the
// open/close state and must handle auto-opening when enabled flips on; this
// eliminates the two-render lag of the mirrored-state pattern. When absent,
// internal state is used and auto-syncs with enabled.
// Both fields must be provided together — the discriminated union enforces
// the pairing at compile time.
type OptionSectionProps = {
  id: string; // aria-controls target id
  title: string; // section label, next to the checkbox
  enabled: boolean; // feature on/off
  onToggleEnabled: () => void;
  summary: string; // one-line current-state summary, always visible (Baymard)
  children: React.ReactNode; // advanced body
  infoTooltip?: string; // optional InfoIconWithTooltip on the header
} & (
  | { open?: never; onOpenChange?: never }
  | { open: boolean; onOpenChange: (open: boolean) => void }
);

// Reusable collapsible feature-section wrapper for the Configure stage (D5,
// docs/plans/todo/scenaria-configure-generation-stage.md). The wrapper IS the
// convention: adding an option = one JSX block. Hand-rolled disclosure
// (aria-expanded + rotating chevron) per the PlanPreview/HistoryAccordion
// precedent — NOT headlessui Disclosure.
//
// The enable checkbox and the open/close toggle are DISTINCT targets: the
// checkbox sits outside the header <button> so turning the feature on and
// expanding its body are separate actions. The body auto-reveals when the
// feature flips on (progressive disclosure); it also stays reachable read-only
// while off, so a user can peek at what enabling would produce without
// committing (D4). Children own their disabled presentation — the wrapper does
// not blank the body while off.
const OptionSection = ({
  id,
  title,
  enabled,
  onToggleEnabled,
  summary,
  children,
  infoTooltip,
  open: openProp,
  onOpenChange,
}: OptionSectionProps) => {
  const controlled = openProp !== undefined;
  const [openInternal, setOpenInternal] = useState(enabled);
  const open = openProp ?? openInternal;

  // Uncontrolled: auto-open when the feature is switched on; force-collapse
  // when switched off. In controlled mode the parent owns this sync.
  useEffect(() => {
    if (!controlled) setOpenInternal(enabled);
  }, [enabled, controlled]);

  const handleToggle = () => {
    if (controlled) {
      onOpenChange?.(!open);
    } else {
      setOpenInternal((v) => !v);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <input
          type="checkbox"
          id={`${id}-toggle`}
          checked={enabled}
          onChange={onToggleEnabled}
          className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-green-600"
        />
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          onClick={handleToggle}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <span className="text-sm font-medium text-gray-700">{title}</span>
          <span className="flex-1 truncate text-xs text-gray-500">{summary}</span>
          <span className="shrink-0 text-gray-400 text-[10px] w-3" aria-hidden>
            {/* Single glyph flipped in CSS: ▸/▾ render at different sizes
                (font fallbacks). */}
            <span className={`inline-block ${open ? "" : "-rotate-90"}`}>▾</span>
          </span>
        </button>
        {infoTooltip && <InfoIconWithTooltip message={infoTooltip} />}
      </div>
      {/* Body renders whenever open=true — including peeks while enabled=false
          (D4). Children own their disabled presentation; the wrapper does NOT
          blank them. Future OptionSection consumers must handle the case where
          their body renders while enabled=false (edit/reset guards etc). */}
      {open && (
        <div id={id} className="px-3 pb-3 pt-1 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

export default OptionSection;
